import { Link } from "react-router-dom";
import { Lightbulb, Check, Minus } from "lucide-react";
import { slugify, getPost } from "@/blog/posts";

/* ------------------------------------------------------------------ */
/* Inline text: **bold**, `code`, [label](url)                          */
/* ------------------------------------------------------------------ */
const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

export function Inline({ text }) {
  const parts = String(text).split(INLINE_RE).filter(Boolean);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-bold text-ink">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={i}
          className="rounded-md bg-ink/[0.07] px-1.5 py-0.5 text-[0.9em] text-ink"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, label, href] = link;
      // Links to articles that are scheduled but not published yet render as
      // plain text, so visitors never land on a missing article. They become
      // real links automatically once the article goes live.
      if (href.startsWith("/blog/") && !getPost(href.slice(6).split(/[?#]/)[0])) {
        return <span key={i}>{label}</span>;
      }
      const cls =
        "font-semibold text-hotpink underline decoration-hotpink/30 underline-offset-4 transition-colors hover:decoration-hotpink";
      return href.startsWith("/") ? (
        <Link key={i} to={href} className={cls}>
          {label}
        </Link>
      ) : (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer" className={cls}>
          {label}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

/* ------------------------------------------------------------------ */
/* Chat block: the one expressive element in an otherwise quiet page   */
/* ------------------------------------------------------------------ */
function ChatBlock({ block }) {
  const { title, caption, messages = [], shop = "yourshop", muted = false } = block;

  return (
    <figure className="my-10" data-testid="blog-chat">
      <div
        className={`mx-auto max-w-[440px] overflow-hidden rounded-[2rem] border bg-white shadow-[0_30px_70px_-34px_rgba(18,16,14,0.45)] ${
          muted ? "border-ink/10" : "border-hotpink/25"
        }`}
      >
        <div className="flex items-center gap-3 border-b border-ink/[0.07] px-5 py-3.5">
          <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-sunset-gradient font-display text-sm font-black text-white">
            {shop.charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-bold text-ink">{shop}</p>
            <p className="text-[11px] text-ink/50">Instagram DM</p>
          </div>
          {title && (
            <span
              className={`flex-none rounded-full px-3 py-1 text-[11px] font-bold ${
                muted ? "bg-ink/[0.07] text-ink/70" : "bg-mint text-ink"
              }`}
            >
              {title}
            </span>
          )}
        </div>

        <div className="space-y-2 bg-cream/70 px-4 py-5">
          {messages.map((m, i) => {
            if (m.from === "time") {
              return (
                <p key={i} className="py-1 text-center text-[11px] font-medium text-ink/45">
                  {m.text}
                </p>
              );
            }
            const isShop = m.from === "shop";
            return (
              <div key={i} className={`flex ${isShop ? "justify-end" : "justify-start"}`}>
                <p
                  className={`max-w-[82%] whitespace-pre-line rounded-[1.25rem] px-4 py-2.5 text-[14px] leading-snug ${
                    isShop
                      ? muted
                        ? "rounded-br-md bg-ink/80 text-white"
                        : "rounded-br-md bg-gradient-to-br from-hotpink to-[#F2542D] text-white"
                      : "rounded-bl-md bg-white text-ink ring-1 ring-ink/10"
                  }`}
                >
                  {m.text}
                </p>
              </div>
            );
          })}
        </div>
      </div>
      {caption && (
        <figcaption className="mx-auto mt-3 max-w-[440px] text-center text-[13px] text-ink/50">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Callout                                                             */
/* ------------------------------------------------------------------ */
const TONES = {
  mint: "bg-mint/45",
  lilac: "bg-lilac/50",
  peach: "bg-peach/30",
};

function Callout({ block }) {
  return (
    <aside
      className={`my-9 flex gap-4 rounded-3xl p-6 ${TONES[block.tone] || TONES.mint}`}
      data-testid="blog-callout"
    >
      <Lightbulb size={20} className="mt-0.5 flex-none text-ink/70" aria-hidden="true" />
      <div>
        {block.title && <p className="font-display text-lg font-black text-ink">{block.title}</p>}
        <p className="mt-1 text-[16px] leading-relaxed text-ink/80">
          <Inline text={block.text} />
        </p>
      </div>
    </aside>
  );
}


/* ------------------------------------------------------------------ */
/* Feature matrix with tick marks and hyphens                          */
/*   { type: "matrix", headers: ["Feature", "Checkout DM", "Other"],   */
/*     rows: [["Comment-to-DM", "yes", "no"], ...], note: "legend" }   */
/* "yes" renders a tick, "no" renders a hyphen, anything else is text. */
/* The first data column is highlighted (it is the one we are          */
/* explaining, usually Checkout DM).                                   */
/* ------------------------------------------------------------------ */
function MatrixCell({ value }) {
  if (value === "yes") {
    return (
      <span className="inline-grid h-7 w-7 place-items-center rounded-full bg-mint text-ink" role="img" aria-label="Yes">
        <Check size={16} strokeWidth={3} aria-hidden="true" />
      </span>
    );
  }
  if (value === "no") {
    return (
      <span className="inline-grid h-7 w-7 place-items-center text-ink/35" role="img" aria-label="Not listed">
        <Minus size={18} strokeWidth={2.5} aria-hidden="true" />
      </span>
    );
  }
  return <span className="text-[13px] font-semibold text-ink/70">{value}</span>;
}

function Matrix({ block }) {
  const { headers, rows, note } = block;
  return (
    <figure className="my-10" data-testid="blog-matrix">
      <div className="overflow-x-auto rounded-3xl border border-ink/10 bg-white">
        <table className="w-full min-w-[560px] border-collapse text-left text-[15px]">
          <thead>
            <tr className="bg-ink text-cream">
              {headers.map((h, i) => (
                <th
                  key={i}
                  className={`px-4 py-3.5 font-display text-sm font-bold ${i === 0 ? "sticky left-0 z-10 bg-ink" : "text-center"} ${i === 1 ? "bg-hotpink" : ""}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/10">
            {rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td
                    key={c}
                    className={`px-4 py-3 align-middle ${
                      c === 0
                        ? "sticky left-0 z-10 bg-white font-semibold leading-snug text-ink"
                        : "text-center"
                    } ${c === 1 ? "bg-mint/25" : ""}`}
                  >
                    {c === 0 ? <Inline text={cell} /> : <MatrixCell value={cell} />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {note && <figcaption className="mt-3 text-[13px] leading-relaxed text-ink/55">{note}</figcaption>}
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Dispatcher                                                          */
/* ------------------------------------------------------------------ */
export function BlogBlock({ block }) {
  switch (block.type) {
    case "p":
      return (
        <p className="mt-5 text-[17.5px] leading-[1.8] text-ink/80">
          <Inline text={block.text} />
        </p>
      );

    case "h2":
      return (
        <h2
          id={slugify(block.text)}
          className="mt-14 scroll-mt-28 font-display text-[1.75rem] leading-[1.1] tracking-tight text-ink md:text-[2.1rem]"
        >
          {block.text}
        </h2>
      );

    case "h3":
      return (
        <h3 className="mt-9 font-display text-xl leading-tight tracking-tight text-ink">
          {block.text}
        </h3>
      );

    case "ul":
      return (
        <ul className="mt-5 space-y-3.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3.5 text-[17px] leading-[1.75] text-ink/80">
              <span
                aria-hidden="true"
                className="mt-[0.7em] h-2 w-2 flex-none rounded-full bg-sunset-gradient"
              />
              <span>
                <Inline text={item} />
              </span>
            </li>
          ))}
        </ul>
      );

    case "ol":
      return (
        <ol className="mt-5 space-y-4">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-4 text-[17px] leading-[1.75] text-ink/80">
              <span
                aria-hidden="true"
                className="mt-1 grid h-7 w-7 flex-none place-items-center rounded-full bg-ink font-display text-xs font-black text-cream"
              >
                {(block.start || 1) + i}
              </span>
              <span>
                <Inline text={item} />
              </span>
            </li>
          ))}
        </ol>
      );

    case "callout":
      return <Callout block={block} />;

    case "quote":
      return (
        <blockquote className="my-12">
          <span
            aria-hidden="true"
            className="text-sunset block font-display text-7xl leading-[0.6]"
          >
            &ldquo;
          </span>
          <p className="mt-2 font-display text-3xl leading-[1.1] tracking-tight text-ink md:text-4xl">
            {block.text}
          </p>
        </blockquote>
      );

    case "figure":
      return (
        <figure className="my-10">
          <img
            src={block.src}
            alt={block.alt}
            width={block.width || 1200}
            height={block.height || 630}
            loading="lazy"
            className="w-full rounded-3xl border border-ink/10 bg-white"
          />
          {block.caption && (
            <figcaption className="mt-3 text-center text-[13px] text-ink/50">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );

    case "chat":
      return <ChatBlock block={block} />;

    case "matrix":
      return <Matrix block={block} />;

    case "table":
      return (
        <div className="my-9 overflow-x-auto rounded-3xl border border-ink/10 bg-white">
          <table className="w-full min-w-[520px] border-collapse text-left text-[15px]">
            <thead>
              <tr className="bg-ink text-cream">
                {block.headers.map((h, i) => (
                  <th key={i} className="px-5 py-3.5 font-display text-sm font-bold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10">
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      className={`px-5 py-3.5 align-top leading-snug ${
                        c === 0 ? "font-bold text-ink" : "text-ink/75"
                      }`}
                    >
                      <Inline text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return null;
  }
}

export function getHeadings(blocks) {
  return blocks
    .filter((b) => b.type === "h2")
    .map((b) => ({ id: slugify(b.text), text: b.text }));
}
